import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { RefreshTokenManager } from '../../utils/refreshTokenManager';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import Co2RoundedIcon from '@mui/icons-material/FactoryOutlined';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ColorModeIconDropdown from '../../shared-theme/ColorModeIconDropdown';
import ApiClient from '../../utils/apiClient';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { getCachedData, setCachedData, clearUserCache, clearConsumptionCache } from '../../utils/cacheUtils';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  fontWeight: 'bold',
  textTransform: 'none',
  color: theme.palette.text.primary,
  minWidth: 'auto',
  px: 2,
  py: 1,
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

interface HeaderProps {
  onMenuSelect: (menuItem: string) => void;
  selectedMenuItem: string;
}

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
  region_id: number;
}

interface MeterInfo {
  consumption_type_id: number;
}

interface PropertyInfo {
  electricity_meters: any[];
  gas_meters: any[];
}

const navigationItems = [
  { text: 'Prices', icon: <CurrencyPoundIcon />, key: 'tariffs' },
  { text: 'Electricity', icon: <BoltRoundedIcon />, key: 'electricity' },
  { text: 'Gas', icon: <SpeedRoundedIcon />, key: 'gas' },
  { text: 'Emissions', icon: <Co2RoundedIcon />, key: 'emissions' }
];

export default function Header({ onMenuSelect, selectedMenuItem }: HeaderProps) {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [meters, setMeters] = React.useState<MeterInfo[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [loadingProgress, setLoadingProgress] = React.useState(0);
  const [showLoadingProgress, setShowLoadingProgress] = React.useState(false);
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Check cache first
        const cachedUserData = getCachedData<UserInfo>('user-current');
        const cachedAccountData = getCachedData<any>('user-account');

        if (cachedUserData && cachedAccountData) {
          // Use cached data
          setUserInfo(cachedUserData);
          
          // Extract meter types from cached account data
          const allMeters: MeterInfo[] = [];
          
          if (cachedAccountData.success && cachedAccountData.account?.properties) {
            cachedAccountData.account.properties.forEach((property: PropertyInfo) => {
              // Add electricity meters (consumption_type_id = 1)
              if (property.electricity_meters && property.electricity_meters.length > 0) {
                property.electricity_meters.forEach(() => {
                  allMeters.push({ consumption_type_id: 1 });
                });
              }
              
              // Add gas meters (consumption_type_id = 2)
              if (property.gas_meters && property.gas_meters.length > 0) {
                property.gas_meters.forEach(() => {
                  allMeters.push({ consumption_type_id: 2 });
                });
              }
            });
          }
          
          setMeters(allMeters);
        } else {
          // Cache miss - fetch from API
          const [userData, accountData] = await Promise.all([
            ApiClient.get('api/users/current'),
            ApiClient.get('api/users/account')
          ]);
          
          // Store in cache
          setCachedData('user-current', userData);
          setCachedData('user-account', accountData);
          
          setUserInfo(userData);
          
          // Extract meter types from account data
          const allMeters: MeterInfo[] = [];
          
          if (accountData.success && accountData.account?.properties) {
            accountData.account.properties.forEach((property: PropertyInfo) => {
              // Add electricity meters (consumption_type_id = 1)
              if (property.electricity_meters && property.electricity_meters.length > 0) {
                property.electricity_meters.forEach(() => {
                  allMeters.push({ consumption_type_id: 1 });
                });
              }
              
              // Add gas meters (consumption_type_id = 2)
              if (property.gas_meters && property.gas_meters.length > 0) {
                property.gas_meters.forEach(() => {
                  allMeters.push({ consumption_type_id: 2 });
                });
              }
            });
          }
          
          setMeters(allMeters);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Listen for loading progress events
  React.useEffect(() => {
    const handleLoadingProgress = (event: CustomEvent) => {
      const { percent_complete } = event.detail;
      setLoadingProgress(percent_complete);
      setShowLoadingProgress(true);
    };

    const handleLoadingComplete = () => {
      setShowLoadingProgress(false);
      setLoadingProgress(0);
      // Trigger dashboard refresh
      window.dispatchEvent(new CustomEvent('dashboardRefresh'));
    };

    window.addEventListener('loadingProgress', handleLoadingProgress as EventListener);
    window.addEventListener('loadingComplete', handleLoadingComplete);

    return () => {
      window.removeEventListener('loadingProgress', handleLoadingProgress as EventListener);
      window.removeEventListener('loadingComplete', handleLoadingComplete);
    };
  }, []);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleAccountClick = () => {
    onMenuSelect('account');
    handleClose();
  };

  const handleUpgradeClick = () => {
    navigate('/upgrade');
    handleClose();
  };

  const handleNavigationClick = (key: string) => {
    onMenuSelect(key);
    setMobileMenuOpen(false);
  };

  const handleLogoutClick = async () => {
    // Set logging out state to prevent UI changes
    setIsLoggingOut(true);
    
    try {
      // Revoke refresh token if it exists
      await RefreshTokenManager.revokeRefreshToken();
    } catch (error) {
      console.error('Error revoking refresh token:', error);
    }
    
    // Clear user cache
    clearConsumptionCache();
    clearUserCache();
    
    // Clear all tokens and redirect to logout route
    RefreshTokenManager.clearTokens();
    window.location.href = '/logout';
  };

  const displayName = userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : 'Loading...';
  const displayEmail = userInfo ? userInfo.email : '';
  
  // Check user access level
  const access = localStorage.getItem("access");
  const isFreeTier = !access || access === 'null' || access === 'free';
  
  // Don't show upgrade button if user is logging out
  const shouldShowUpgrade = isFreeTier && !isLoggingOut;

  // Determine which navigation items to show based on meter types
  const hasElectricityMeter = meters.some(meter => meter.consumption_type_id === 1);
  const hasGasMeter = meters.some(meter => meter.consumption_type_id === 2);
  const hasAnyMeter = hasElectricityMeter || hasGasMeter;

  // Filter navigation items based on meter availability
  const filteredNavigationItems = navigationItems.filter(item => {
    switch (item.key) {
      case 'electricity':
        return hasElectricityMeter;
      case 'gas':
        return hasGasMeter;
      case 'emissions':
        return hasAnyMeter; // Only show emissions if user has at least one meter
      default:
        return true; // Always show insights and electricity rates
    }
  });

  return (
    <StyledAppBar 
      position="static"
      sx={{
        position: { xs: 'static', md: 'fixed' },
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '1200px',
        width: '100%',
        mx: 'auto'
      }}>
        {/* Main Header Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: { xs: 1, sm: 2, md: 3 },
          minHeight: '64px'
        }}>
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {filteredNavigationItems.map((item) => (
              <StyledButton
                key={item.key}
                onClick={() => onMenuSelect(item.key)}
                className={selectedMenuItem === item.key ? 'Mui-selected' : ''}
                startIcon={item.icon}
              >
                {item.text}
              </StyledButton>
            ))}
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <IconButton
              onClick={handleMobileMenuToggle}
              size="medium"
              aria-controls={mobileMenuOpen ? 'mobile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={mobileMenuOpen ? 'true' : undefined}
              sx={{ 
                '& .MuiSvgIcon-root': {
                  fontSize: '1.8rem'
                }
              }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

          {/* Right side - Loading progress, User avatar and theme selector */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {showLoadingProgress && (
              <Tooltip title={`Loading your energy data. ${Math.round(loadingProgress)}% complete.`}>
                <Box sx={{ mr: 2 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={loadingProgress} 
                    size={24}
                    thickness={4}
                  />
                </Box>
              </Tooltip>
            )}
            
            <ColorModeIconDropdown />
            
            <IconButton
              onClick={handleAvatarClick}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar
                alt={displayName}
                sx={(theme) => ({ 
                  width: 32, 
                  height: 32,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.secondary,
                  }
                })}
              />
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              slotProps={{
                paper: {
                  sx: {
                    my: '4px',
                    minWidth: 200,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {displayEmail}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleAccountClick}>
                <ListItemIcon>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Account</ListItemText>
              </MenuItem>
              {shouldShowUpgrade && (
                <MenuItem onClick={handleUpgradeClick}>
                  <ListItemIcon>
                    <UpgradeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Upgrade</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogoutClick}>
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Mobile Navigation Dropdown */}
        <Collapse in={mobileMenuOpen} timeout="auto" unmountOnExit>
          <Box sx={{ 
            px: { xs: 1, sm: 2, md: 3 },
            pb: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              mt: 1
            }}>
              {filteredNavigationItems.map((item) => (
                <Button
                  key={item.key}
                  onClick={() => handleNavigationClick(item.key)}
                  startIcon={item.icon}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: 1,
                    backgroundColor: selectedMenuItem === item.key 
                      ? 'primary.main' 
                      : 'transparent',
                    color: selectedMenuItem === item.key 
                      ? 'primary.contrastText' 
                      : 'text.primary',
                    '&:hover': {
                      backgroundColor: selectedMenuItem === item.key 
                        ? 'primary.dark' 
                        : 'action.hover',
                    },
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </StyledAppBar>
  );
}
